import { useState, useEffect } from "react";
import { Box, Typography, useTheme, IconButton } from "@mui/material";
import {
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineContent,
  TimelineConnector,
} from "@mui/lab";
import { HubRounded } from "@mui/icons-material";

function ToolCallBlock({ block, isStreaming = false, showIcon = true }) {
  const { attributes } = block;
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  // Parse arguments if it's a string, otherwise use as-is
  const parsedArguments =
    typeof attributes.arguments === "string"
      ? (() => {
          try {
            return JSON.parse(attributes.arguments);
          } catch (e) {
            return attributes.arguments;
          }
        })()
      : attributes.arguments;

  // Format tool name for display
  const getFormattedToolName = () => {
    // Prefer server-provided display name
    if (attributes.display_name) return attributes.display_name;
    const toolName = attributes.name;

    switch (toolName) {
      case "grep_search":
        return "Searching keywords";
      case "read_file":
        return "Reading file";
      case "docbase_search":
        return isStreaming ? "Searching documents" : "Searched documents";
      case "docbase_hybrid_search":
        return isStreaming ? "Searching documents" : "Searched documents";
      case "glob_file_search":
        return "Searching files";
      case "list_dir":
        return "Listing directory";
      case "read_excel_file":
        return "Reading Excel file";
      default:
        return toolName;
    }
  };

  // Format arguments for display (returns formatted string or null)
  const getFormattedArguments = () => {
    if (!parsedArguments || typeof parsedArguments !== "object") {
      return null;
    }

    const toolName = attributes.name;

    // Prefer server-provided display arguments (single-line, preformatted)
    if (
      typeof attributes.display_arguments === "string" &&
      attributes.display_arguments.trim()
    ) {
      return attributes.display_arguments.trim();
    }

    const truncate = (value, maxLen = 200) => {
      if (typeof value !== "string") return value;
      return value.length > maxLen ? `${value.slice(0, maxLen - 1)}…` : value;
    };

    const formatKeywords = (grep) => {
      if (!grep || typeof grep !== "string") return null;
      const raw = grep
        .split("|")
        .map((t) => t.trim())
        .filter(Boolean);
      const seen = new Set();
      const deduped = [];
      for (const token of raw) {
        const key = token.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(token);
        }
      }
      return deduped.join(", ");
    };

    switch (toolName) {
      case "grep_search":
        return parsedArguments.query || null;

      case "read_file":
        if (parsedArguments.target_file) {
          // Remove file extension for cleaner display
          return parsedArguments.target_file.replace(
            /\.(mmd|md|txt|pdf|docx?)$/i,
            ""
          );
        }
        return null;

      case "docbase_search":
        return truncate(parsedArguments.query || null);

      case "docbase_hybrid_search": {
        const q = (parsedArguments.query || "").trim();
        const kw = formatKeywords(parsedArguments.grep_query);
        if (!q && !kw) return null;
        return (
          <>
            <br />
            <Typography component="span" variant="body2" color="text.secondary">
              Query:
            </Typography>{" "}
            <Typography component="span" variant="body2">
              {q || "-"}
            </Typography>
            {kw && (
              <>
                <br />
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary">
                  Keywords:
                </Typography>{" "}
                <Typography component="span" variant="body2">
                  {kw}
                </Typography>
              </>
            )}
          </>
        );
      }

      case "glob_file_search":
        return parsedArguments.glob_pattern || null;

      case "list_dir":
        return parsedArguments.target_directory || null;

      case "read_excel_file":
        const parts = [];
        if (parsedArguments.workbook_path) {
          // Extract just the filename from the path
          parts.push(parsedArguments.workbook_path.split("/").pop());
        }
        if (parsedArguments.sheet_name) {
          parts.push(`sheet: ${parsedArguments.sheet_name}`);
        }
        if (parsedArguments.range_address) {
          parts.push(`range: ${parsedArguments.range_address}`);
        }
        return parts.length > 0 ? parts.join(", ") : null;

      default:
        // For unknown tools, show all arguments
        return Object.entries(parsedArguments)
          .map(
            ([key, value]) =>
              `${key}: ${
                typeof value === "object"
                  ? JSON.stringify(value)
                  : String(value)
              }`
          )
          .join(", ");
    }
  };

  const formattedArgs = getFormattedArguments();

  return (
    <Box
      sx={{
        display: "flex",
        cursor: "pointer",
        transition: "background 0.2s",
        borderRadius: 8,
      }}
      alignItems={"flex-start"}
      onClick={() => setExpanded(!expanded)}>
      {showIcon && (
        <Box sx={{ mr: 1, display: "inline-flex", flexShrink: 0 }}>
          <HubRounded
            sx={{ fontSize: 16, color: theme.palette.action.disabled }}
          />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
          <Typography component="span" variant="body2" color="text.primary">
            {getFormattedToolName()}
          </Typography>
          {formattedArgs && (
            <>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary">
                {" "}
                —{" "}
              </Typography>
              {typeof formattedArgs === "string" ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary">
                  {formattedArgs}
                </Typography>
              ) : (
                formattedArgs
              )}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

export function TimelineItemWrapper({ children, isLast = false }) {
  const theme = useTheme();
  return (
    <TimelineItem sx={{ minHeight: 0 }}>
      <TimelineSeparator>
        <TimelineDot
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            margin: 0,
            paddingX: 0,
            border: "none",
          }}>
          <HubRounded
            sx={{ fontSize: 16, color: theme.palette.action.disabled }}
          />
        </TimelineDot>
        {!isLast && (
          <TimelineConnector
            sx={{
              backgroundColor: theme.palette.action.disabled,
              minHeight: 32,
            }}
          />
        )}
      </TimelineSeparator>
      <TimelineContent sx={{ padding: 0, paddingLeft: 1, paddingTop: 0.5 }}>
        {children}
      </TimelineContent>
    </TimelineItem>
  );
}

export default ToolCallBlock;
